'use client';

import { FC, useMemo } from 'react';

import {
  EMetric,
  metricDictionary
} from 'api/useAnalytics';
import { DATE_DAY_MONTH_FORMAT } from 'constants/constants';
import _ from 'lodash';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import { BaseStatistic, EStatisticType } from 'views/Statistics/StatisticsView.types';

import { AnalyticsLoadingBox } from 'components/AnalyticsBox/AnalyticsLoadingBox';
import {
  getChartLabelFromDate,
  getFormattedValueByType
} from 'components/AnalyticsBox/utils';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from 'components/ui/card';
import { ChartContainer } from 'components/ui/chart';

import { AttributeTrendsTooltip } from './AttributeTrendsTooltip';
import { formatTrendsDataForChart } from './utils';

const COLORS = [
  'var(--base-chart-1)',
  'var(--base-chart-2)',
  'var(--base-chart-3)',
  'var(--base-chart-4)',
  'var(--base-chart-5)',
];

type AttributeTrendsBoxProps = {
  isLoading: boolean;
  metricName: EMetric;
  trends: BaseStatistic[];
  xAxisDateFormat?: string;
};
 

export const AttributeTrendsBox: FC<AttributeTrendsBoxProps> = ({
  isLoading,
  metricName,
  trends,
  xAxisDateFormat = DATE_DAY_MONTH_FORMAT
}) => {
  const chartData = useMemo(() => formatTrendsDataForChart(trends, metricName), [trends, metricName]);

    const tickCount: number | undefined = useMemo(() => {
    const allValues = chartData.flatMap(({date, ...rest}) => Object.values(rest)).filter((value) => value !== undefined);
    const uniqueValues = _.uniq(allValues);
    return uniqueValues.length < 5 ? Math.max(uniqueValues.length, 2) : undefined;
  }, [chartData]);

  const trendKeys = useMemo(() => trends.map((t) => t.name), [trends]);
  const colors = useMemo(
    () =>
      trendKeys.reduce(
        (acc, key, index) => {
          acc[key] = COLORS[index % COLORS.length];
          return acc;
        },
        {} as { [key: string]: string }
      ),
    [trendKeys]
  );

  const {
    title,
    description: subtitle,
    type: metricType
  } = metricDictionary[metricName];

  if (isLoading) return <AnalyticsLoadingBox />;

  const tickStyle = {
    fill: 'var(--base-muted-foreground)',
    fontSize: '10px',
    fontFamily: 'var(--typography-font-family-font-sans, Inter)',
    fontStyle: 'normal',
    fontWeight: 'var(--font-weight-normal, 400)'
  };

  return (
    <Card className="p-[24px] shadow-none">
      <CardHeader className="p-0">
        <CardTitle>{title}</CardTitle>
        <CardDescription>{subtitle}</CardDescription>
      </CardHeader>
      <CardContent className="p-0 pt-[22px]">
        <ChartContainer
          config={{}}
          className="min-h-[220px] max-h-[220px] w-full"
        >
          <LineChart margin={{ left: 0 }} data={chartData}>
            <CartesianGrid vertical={false} stroke="var(--base-border)" />
            <XAxis
              tickFormatter={(value) =>
                getChartLabelFromDate(value, xAxisDateFormat)
              }
              axisLine={false}
              tickLine={false}
              dataKey="date"
              tick={tickStyle}
              dy={10}
            />
            <YAxis
              tickFormatter={(value) =>
                getFormattedValueByType(
                  value,
                  metricType ?? EStatisticType.NUMBER
                )
              }
              tick={tickStyle}
              axisLine={false}
              tickLine={false}
              tickMargin={10}
              tickCount={tickCount}
            />
            <Tooltip
              content={
                <AttributeTrendsTooltip
                  metricType={metricType ?? EStatisticType.NUMBER}
                  colors={colors}
                />
              }
              wrapperStyle={{ outline: 'none', border: 'none' }}
              cursor={{ stroke: 'var(--base-border)', strokeWidth: 2 }}
            />
            <Legend
              formatter={(value) => (
                <span className="text-xs text-base-foreground">{value}</span>
              )}
              iconSize={10}
              iconType="square"
              wrapperStyle={{ paddingTop: '24px'}}
            />
            {trendKeys.map((key) => (
              <Line
                key={key}
                type="monotone"
                dataKey={key}
                stroke={colors[key]}
                strokeWidth={1}
                dot={false}
                activeDot={{
                  r: 4,
                  stroke: 'var(--base-white)',
                  strokeWidth: 1,
                  fill: colors[key]
                }}
              />
            ))}
          </LineChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
};